import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import PropertyCardWrapper from "../propertyCard/components/propertyCardWrapper/PropertyCardWrapper";
import styles from "./Mapview.module.scss";
import GoogleMapReact, { fitBounds } from "google-map-react";
import useFilter from "../../../../hooks/useFilter";
import { useAppSelector } from "../../../../hooks/reduxHooks";
import {
  selectCurrency,
  selectEndDate,
  selectIniDate,
} from "../../../../redux/slices/enviromentSlice/EnviromentSlice.selectors";
import { getMinPrice } from "../../../../utils/getPriceUtils";
import PropertyMarker from "./components/propertyMarker/PropertyMarker";
import { useEffect } from "react";
import useOnResize from "../../../../hooks/useOnResize";
import CustomStars from "../../../../components/customStars/CustomStars";
import parse from "html-react-parser";
import { Link } from "react-router-dom";
import StickyComponent from "../../../../components/stickyComponent/StickyComponent";
import { HotelDataListI } from "../../../../redux/slices/enviromentSlice/EnviromentSlice.type";
import { Typography } from "@mui/material";
import { MdErrorOutline } from "@react-icons/all-files/md/MdErrorOutline";
import { FilterSortType, filterStateI } from "../../contexts/filterContext";
import { useWidgetConfigContext } from "src/contexts/widgetConfig/WidgetConfig.context";

export interface boundsI {
  nw: {
    lat: string;
    lng: string;
  };
  se: {
    lat: string;
    lng: string;
  };
}

interface clientPositionInDivI {
  x: number;
  y: number;
}

export interface currentMapPositionI {
  center: {
    lat: string;
    lng: string;
  };
  zoom: number;
}

export interface positionI {
  lat: string;
  lng: string;
  id: string;
}

export interface ActiveHotelI {
  id: string;
  name: string;
  reviews: string;
  imagePath: string | undefined;
  unitsFree: string;
  nights?: string;
  price: string;
  lat: string | undefined;
  lng: string | undefined;
}

const defaultActiveHotel: ActiveHotelI = {
  id: "",
  name: "",
  reviews: "",
  imagePath: "",
  unitsFree: "",
  nights: "",
  price: "",
  lat: "",
  lng: "",
};

export type PositionsT = "top" | "right" | "bottom" | "left";

export const POSITIONS: {
  TOP: "top";
  RIGHT: "right";
  BOTTOM: "bottom";
  LEFT: "left";
} = {
  TOP: "top",
  RIGHT: "right",
  BOTTOM: "bottom",
  LEFT: "left",
};

const MINICARD_SIZES = {
  WIDTH: 250,
  HEIGTH: 250,
};

const xMediumBreakpoint = 1135;
const mediumBreakpoint = 425;

const Mapview: React.FC<{
  filterState: filterStateI;
  hotelDataList: HotelDataListI[];
}> = (props) => {
  const { filterState, hotelDataList } = props;

  const currency = useAppSelector(selectCurrency);
  const iniDate = useAppSelector(selectIniDate);
  const endDate = useAppSelector(selectEndDate);

  const { gmapToken } = useWidgetConfigContext();

  const { t } = useTranslation();

  const { filteredArray } = useFilter(hotelDataList, filterState);

  const [miniCardPosition, setMiniCardPosition] = useState<Array<PositionsT>>(
    []
  );
  const [activeHotel, setActiveHotel] =
    useState<ActiveHotelI>(defaultActiveHotel);
  const [isMiniCardOpen, setIsMiniCardOpen] = useState(false);
  const [isBiggerThanXm, setIsBiggerThanXm] = useState(
    xMediumBreakpoint < window.innerWidth
  );
  const [isBiggerThanM, setIsBiggerThanM] = useState(
    mediumBreakpoint < window.innerWidth
  );
  const [isPropertyListDisplaying, setIsPropertyListDisplaying] =
    useState(true);
  const [currentMapPosition, setCurrentMapPosition] =
    useState<currentMapPositionI>(() => {
      const getDefaultCenterCoordinates = (hotelArray: HotelDataListI[]) => {
        const hotelIndex = hotelArray.findIndex(
          (hotel) => hotel.latitude && hotel.longitude && hotel.available
        );

        if (hotelIndex === -1) {
          return { defaultCoordinatesError: t("errors.noUbicationWasFound") };
        }

        return {
          lat: hotelArray[hotelIndex].latitude!,
          lng: hotelArray[hotelIndex].longitude!,
        };
      };

      const { lat, lng } = getDefaultCenterCoordinates(hotelDataList);

      return {
        center: {
          lat: lat || "",
          lng: lng || "",
        },
        zoom: 7,
      };
    });
  const mapContainer = useRef<HTMLDivElement>(null);
  const miniCardEl = useRef<HTMLDivElement>(null);
  const isDraggin = useRef<boolean>(false);

  const getDefaultCenterCoordinates = (hotelArray: HotelDataListI[]) => {
    const hotelIndex = hotelArray.findIndex(
      (hotel) => hotel.latitude && hotel.longitude && hotel.available
    );

    if (hotelIndex === -1) {
      return { defaultCoordinatesError: t("errors.noUbicationWasFound") };
    }

    return {
      lat: +hotelArray[hotelIndex].latitude!,
      lng: +hotelArray[hotelIndex].longitude!,
    };
  };

  const { defaultCoordinatesError } =
    getDefaultCenterCoordinates(hotelDataList);

  useOnResize(() => {
    setIsBiggerThanXm(xMediumBreakpoint < window.innerWidth);
    setIsBiggerThanM(mediumBreakpoint < window.innerWidth);
  });

  const allMarkersPositions = filteredArray
    .filter((property) => property?.available)
    .reduce((acum, current) => {
      if (current && current.longitude && current.latitude) {
        const newPositionObject: positionI = {
          id: current.id,
          lat: current.latitude,
          lng: current.longitude,
        };

        return (acum = [...acum, newPositionObject]);
      }
      return acum;
    }, [] as positionI[]);

  useEffect(() => {
    if (!isBiggerThanXm && isPropertyListDisplaying)
      setIsPropertyListDisplaying(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBiggerThanXm]);

  useEffect(() => {
    if (allMarkersPositions.length > 1) {
      const boundMarkers = allMarkersPositions.reduce((acum, current) => {
        if (+current.lat > +acum?.nw?.lat || !acum?.nw?.lat) {
          acum = { ...acum, nw: { ...acum.nw, lat: current.lat } };
        }
        if (+current.lng < +acum?.nw?.lng || !acum?.nw?.lng) {
          acum = { ...acum, nw: { ...acum.nw, lng: current.lng } };
        }
        if (+current.lat < +acum?.se?.lat || !acum?.se?.lat) {
          acum = { ...acum, se: { ...acum.se, lat: current.lat } };
        }
        if (+current.lng > +acum?.se?.lng || !acum?.se?.lng) {
          acum = { ...acum, se: { ...acum.se, lng: current.lng } };
        }
        return acum;
      }, {} as boundsI);

      const size = {
        width: mapContainer.current?.offsetWidth,
        height: mapContainer.current?.offsetHeight,
      };

      if (
        boundMarkers?.nw?.lat &&
        boundMarkers?.nw?.lng &&
        boundMarkers?.se?.lng &&
        boundMarkers?.se?.lat
      ) {
        const newCurrentMapPosition = fitBounds(
          boundMarkers as any,
          size as any
        );

        setCurrentMapPosition(newCurrentMapPosition as any);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filteredArray,
    mapContainer.current?.offsetWidth,
    mapContainer.current?.offsetHeight,
  ]);

  const handleCloseMiniCard = () => {
    setActiveHotel(defaultActiveHotel);
    setIsMiniCardOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (isDraggin.current) return;
      if (e.target instanceof HTMLElement) {
        if (
          !e.target.classList.contains("marker") &&
          !miniCardEl.current?.contains(e.target)
        )
          return handleCloseMiniCard();
      }
    };

    if (isMiniCardOpen) {
      window.addEventListener("click", handleClickOutside);
      window.addEventListener("mousedown", () => (isDraggin.current = false));
      window.addEventListener("mousemove", () => (isDraggin.current = true));
    }
    return () => {
      window.removeEventListener("click", handleClickOutside);
      window.addEventListener("mousedown", () => (isDraggin.current = false));
      window.addEventListener("mousemove", () => (isDraggin.current = true));
    };
  }, [isMiniCardOpen, isDraggin]);

  useEffect(() => {
    const handleClickPosition = (e: MouseEvent) => {
      if (!mapContainer.current) return;
      // const targetEl = e.target as Node;
      const rect = mapContainer.current.getBoundingClientRect();
      const mapSize = {
        height: mapContainer.current.offsetHeight,
        width: mapContainer.current.offsetWidth,
      };

      if ((e.target as HTMLDivElement).classList.contains("marker")) {
        const clientPositionInDiv: clientPositionInDivI = {
          x: e.clientX - rect.left,
          y: e.clientY - rect.top,
        };

        setMiniCardPosition(getMiniCardPosition(clientPositionInDiv, mapSize));
      }
    };

    window.addEventListener("click", handleClickPosition);
    return () => {
      window.removeEventListener("click", handleClickPosition);
    };
  }, [mapContainer]);

  const getMiniCardPosition = (
    clientPosition: clientPositionInDivI,
    mapSize: { height: number; width: number }
  ) => {
    const { x, y } = clientPosition;
    const { width: mapWidth, height: mapHeight } = mapSize;

    let position: PositionsT[] = [];

    if (x + MINICARD_SIZES.WIDTH > mapWidth) {
      position.push(POSITIONS.LEFT);
    } else {
      position.push(POSITIONS.RIGHT);
    }
    if (y + MINICARD_SIZES.HEIGTH > mapHeight) {
      position.push(POSITIONS.TOP);
    } else {
      position.push(POSITIONS.BOTTOM);
    }

    return position;
  };

  const createMapOptions = () => ({
    gestureHandling: "greedy",
  });

  return (
    <div className={styles.mainContainer}>
      <div
        className={`${styles.mapview_container} ${
          isPropertyListDisplaying
            ? styles.mapview_container_opened
            : styles.isFullWidth
        }`}
      >
        {isPropertyListDisplaying && isBiggerThanXm && (
          <div className={`${styles.mapview_list}`}>
            <PropertyCardWrapper
              filterState={filterState}
              isColumn
              setActiveHotel={setActiveHotel}
              setIsMiniCardOpen={setIsMiniCardOpen}
            >
              {hotelDataList}
            </PropertyCardWrapper>
          </div>
        )}
        <div className={`${styles.mapview_map_container}`} ref={mapContainer}>
          <StickyComponent>
            <div
              className={`${styles.mapview_map} ${
                !isPropertyListDisplaying && styles.isFullWidth
              }  ${isPropertyListDisplaying && styles.mapview_map_opened}`}
            >
              {!defaultCoordinatesError ? (
                <>
                  {isBiggerThanXm && (
                    <div
                      className={`${styles.floating_arrow} ${
                        !isPropertyListDisplaying && styles.floating_arrow_open
                      }`}
                      onClick={() =>
                        setIsPropertyListDisplaying(!isPropertyListDisplaying)
                      }
                    >
                      <img
                        src="https://booking.availroom.com/v2/assets/icons/leftArrow.svg"
                        alt="toggle property list"
                      />
                      {!isPropertyListDisplaying && (
                        <p>{t("labels.showPropertyList")}</p>
                      )}
                    </div>
                  )}
                  <GoogleMapReact
                    options={createMapOptions}
                    bootstrapURLKeys={{
                      key: gmapToken,
                    }}
                    defaultCenter={{
                      lat: +currentMapPosition.center.lat,
                      lng: +currentMapPosition.center.lng,
                    }}
                    defaultZoom={7}
                    zoom={
                      allMarkersPositions.length > 1
                        ? currentMapPosition.zoom
                        : 15
                    }
                  >
                    {filteredArray.length !== 0 &&
                      filteredArray.map(
                        (hotel) =>
                          hotel?.available && (
                            <PropertyMarker
                              isBiggerThanXm={isBiggerThanXm}
                              isBiggerThanM={isBiggerThanM}
                              key={hotel?.id}
                              lat={hotel?.latitude}
                              lng={hotel?.longitude}
                              miniCardEl={miniCardEl}
                              isMiniCardOpen={isMiniCardOpen}
                              miniCardPosition={miniCardPosition}
                              hotel={{
                                id: hotel?.id,
                                name: hotel?.name,
                                reviews: hotel?.opinionReview,
                                imagePath: hotel?.imagePathList[0],
                                unitsFree: hotel?.unitsFree,
                                nights: hotel?.nights,
                                lat: hotel?.latitude,
                                lng: hotel.longitude,
                                price: `${currency}
                    ${
                      filterState.isFreeCancelable === FilterSortType.UNSET
                        ? getMinPrice([
                            hotel?.priceRefundable,
                            hotel?.priceNotRefundable,
                          ])
                        : filterState.isFreeCancelable
                        ? hotel?.priceRefundable
                        : hotel?.priceNotRefundable
                    }`,
                              }}
                              activeHotel={activeHotel}
                              setActiveHotel={setActiveHotel}
                              setIsMiniCardOpen={setIsMiniCardOpen}
                            />
                          )
                      )}
                  </GoogleMapReact>
                  {!isBiggerThanM && isMiniCardOpen && (
                    <Link
                      to={`${document.location.pathname}?&iniDate=${iniDate}&endDate=${endDate}&approute=property/${activeHotel.id}`}
                    >
                      <div className={styles.miniCard_container}>
                        <img src={activeHotel.imagePath} alt="" />
                        <div className={styles.wrapper}>
                          <div className={styles.card_stars}>
                            <CustomStars opinionReview={+activeHotel.reviews} />
                          </div>
                          <div className={styles.title}>
                            {parse(activeHotel.name)}
                          </div>
                          <p className={styles.card_price}>
                            {activeHotel.price}
                          </p>
                          <p>
                            ({activeHotel.nights} {t("property.nights")})
                          </p>
                        </div>
                      </div>
                    </Link>
                  )}
                </>
              ) : (
                <div className={styles.error}>
                  <MdErrorOutline fontSize="2rem" color="red" />
                  <Typography>{defaultCoordinatesError}</Typography>
                </div>
              )}
            </div>
          </StickyComponent>
        </div>
      </div>
    </div>
  );
};

export default Mapview;
